import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, GlobeSimple } from "@phosphor-icons/react";

const pricingData = {
  uk: {
    perEntry: { fee: "£1", net: "£9", example: "£10", currency: "£" },
    oneOff: { price: "£195", processing: "~£0.35/entry" },
    label: "UK",
  },
  ie: {
    perEntry: { fee: "€1", net: "€9", example: "€10", currency: "€" },
    oneOff: { price: "€249", processing: "~€0.40/entry" },
    label: "Ireland",
  },
};

function detectRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && tz.toLowerCase().includes("dublin")) return "ie";
    const lang = navigator.language || "";
    if (lang.toLowerCase().includes("ga") || lang.toLowerCase().includes("ie")) return "ie";
  } catch {}
  return "uk";
}

export default function Pricing() {
  const [region, setRegion] = useState("uk");

  useEffect(() => {
    setRegion(detectRegion());
  }, []);

  const current = pricingData[region];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-10 lg:py-14 px-6 bg-white border-t border-[#1D3557]/5"
    >
      <div className="max-w-6xl mx-auto">
        <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-3">
          Pricing
        </p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-[#1D3557] mb-3">
          Two Ways To Pay
        </h2>
        <p className="font-body text-base text-[#1D3557]/55 leading-relaxed max-w-lg mb-3">
          Choose what works best for your club. No subscriptions. No contracts. Cancel anytime.
        </p>
        <p className="font-body text-base text-[#1D3557]/65 leading-relaxed max-w-lg mb-6">
          One club raised <strong className="text-[#1D3557]">€2,400</strong> in just 3 weeks with our Masters 2026 Sweepstake — <strong className="text-[#1D3557]">with zero upfront cost</strong>.
        </p>

        {/* Region toggle */}
        <div className="flex items-center gap-2 mb-8" data-testid="region-toggle">
          <GlobeSimple size={16} className="text-[#1D3557]/30" />
          <div className="flex border border-[#1D3557]/10">
            <button
              onClick={() => setRegion("uk")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-colors ${
                region === "uk"
                  ? "bg-[#1D3557] text-white"
                  : "text-[#1D3557]/40 hover:text-[#1D3557]"
              }`}
              data-testid="region-toggle-uk"
            >
              UK
            </button>
            <button
              onClick={() => setRegion("ie")}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-colors ${
                region === "ie"
                  ? "bg-[#1D3557] text-white"
                  : "text-[#1D3557]/40 hover:text-[#1D3557]"
              }`}
              data-testid="region-toggle-ie"
            >
              Ireland
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl"
        >
          {/* Pay per entry */}
          <div className="border-2 border-[#E63946] p-6 lg:p-8 relative" data-testid="pricing-card-per-entry">
            <span className="absolute top-0 right-0 bg-[#E63946] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">
              Popular
            </span>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-1">
              {current.perEntry.currency}0 Upfront
            </p>
            <h3 className="font-heading font-bold text-2xl tracking-tight text-[#1D3557] mb-3">
              Pay per entry
            </h3>
            <p className="font-body text-sm text-[#1D3557]/60 leading-relaxed mb-4">
              {current.perEntry.fee} per entry — includes all payment fees and TargetFunder fees. Your club nets <strong className="text-[#1D3557]">{current.perEntry.net} of every {current.perEntry.example}</strong> entry, paid straight into your bank.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#E63946] mt-0.5 flex-shrink-0" />
                <span className="font-body text-xs text-[#1D3557]/55">No upfront cost</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#E63946] mt-0.5 flex-shrink-0" />
                <span className="font-body text-xs text-[#1D3557]/55">All fees included in the {current.perEntry.fee}</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#E63946] mt-0.5 flex-shrink-0" />
                <span className="font-body text-xs text-[#1D3557]/55">Zero risk — only pay when members enter</span>
              </div>
            </div>
          </div>

          {/* One-off fee */}
          <div className="border border-[#1D3557]/10 p-6 lg:p-8" data-testid="pricing-card-one-off">
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#1D3557]/40 mb-1">
              One-Off Fee
            </p>
            <h3 className="font-heading font-bold text-2xl tracking-tight text-[#1D3557] mb-3">
              Pay {current.oneOff.price} once
            </h3>
            <p className="font-body text-sm text-[#1D3557]/60 leading-relaxed mb-4">
              One-time setup fee. After that, you keep <strong className="text-[#1D3557]">100% of every entry</strong> — no fee per entry, ever (Stripe payment processing still applies, {current.oneOff.processing}).
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#1D3557]/40 mt-0.5 flex-shrink-0" />
                <span className="font-body text-xs text-[#1D3557]/55">Best value for larger clubs</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#1D3557]/40 mt-0.5 flex-shrink-0" />
                <span className="font-body text-xs text-[#1D3557]/55">Keep 100% of entry fees</span>
              </div>
              <div className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[#1D3557]/40 mt-0.5 flex-shrink-0" />
                <span className="font-body text-xs text-[#1D3557]/55">Only standard Stripe processing applies</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="mt-6">
          <a
            href="https://app.targetfunder.com/demo/create"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#E63946] text-white font-bold uppercase tracking-widest px-6 py-3 text-xs hover:bg-[#d32f3c] transition-colors"
            data-testid="pricing-cta-btn"
          >
            Try Free Demo
          </a>
        </div>
      </div>
    </section>
  );
}
